import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiHelpers } from "../lib/api";
import {
  STATIC_MODULE_CATALOG,
  isCatalogFetchFallbackError,
  logModuleCatalogFallback,
  ModuleCatalogParseError,
  parseModuleCatalogResponse,
  setModuleCatalog,
  type ModuleCatalog,
} from "../lib/moduleCatalog";
import { applyModuleCatalogToGates } from "../lib/moduleGates";
import { useAuthStore } from "../stores/authStore";

export const moduleCatalogQueryKey = ["access-requests", "module-catalog"] as const;

async function fetchModuleCatalog(): Promise<ModuleCatalog> {
  try {
    const res = await apiHelpers.accessRequests.modules();
    try {
      return parseModuleCatalogResponse(res.data);
    } catch (parseError) {
      if (parseError instanceof ModuleCatalogParseError) {
        logModuleCatalogFallback(
          "Using static module catalog fallback after parse failure",
          parseError.message,
        );
        return { ...STATIC_MODULE_CATALOG };
      }
      throw parseError;
    }
  } catch (error) {
    if (isCatalogFetchFallbackError(error)) {
      logModuleCatalogFallback("Using static module catalog fallback after fetch failure", error);
      return { ...STATIC_MODULE_CATALOG };
    }
    throw error;
  }
}

export function useModuleCatalog() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authLoading = useAuthStore((s) => s.isLoading);

  return useQuery({
    queryKey: moduleCatalogQueryKey,
    queryFn: fetchModuleCatalog,
    enabled: isAuthenticated && !authLoading,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: STATIC_MODULE_CATALOG,
  });
}

/** Applies catalog snapshot for sync permission/requestable flags (no UI). */
export function ModuleCatalogSync() {
  const { data } = useModuleCatalog();

  useEffect(() => {
    if (!data) return;
    setModuleCatalog(data);
    applyModuleCatalogToGates(data);
  }, [data]);

  return null;
}
